import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';
import { Button } from '../components/Button';
import { api } from '../lib/api';
import { haptic } from '../lib/telegram';
import { cn } from '../lib/utils';

interface Props {
  onDone(): void;
}

const SLIDES = [
  {
    eyebrow: 'Predict',
    title: 'Pick winners, scores, BTTS',
    body: 'Every fixture is open until kickoff. Tap a card to lock in your call.',
    icon: '🎯',
  },
  {
    eyebrow: 'Score',
    title: 'Streaks stack',
    body: 'Earn points for every correct market. 3-day streak gives +1, 7-day gives +2. Boosts double everything.',
    icon: '📈',
  },
  {
    eyebrow: 'Climb',
    title: 'Beat your mates',
    body: 'Private leagues for the group chat, country leagues for your nation, a global leaderboard for the bold.',
    icon: '🏆',
  },
] as const;

export function Onboarding({ onDone }: Props) {
  const [idx, setIdx] = useState(0);
  const qc = useQueryClient();

  // Fire-and-forget — onboarding ledger is best-effort and must never block
  // the user behind a flaky network or a D1 timeout. We dismiss immediately
  // and let the markOnboarded request resolve in the background.
  function finish() {
    haptic.success();
    onDone();
    void api
      .markOnboarded()
      .then(() => qc.invalidateQueries({ queryKey: ['me'] }))
      .catch((err) => console.warn('markOnboarded failed (non-blocking)', err));
  }

  const slide = SLIDES[idx]!;
  const isLast = idx === SLIDES.length - 1;

  function advance() {
    haptic.select();
    if (isLast) {
      finish();
    } else {
      setIdx((i) => Math.min(i + 1, SLIDES.length - 1));
    }
  }

  function skip() {
    haptic.tap();
    finish();
  }

  return (
    <div className="mx-auto max-w-md min-h-full px-6 pt-10 pb-16 flex flex-col">
      <div className="flex justify-end">
        <button
          onClick={skip}
          className="font-display-alt text-xs uppercase tracking-widest text-text-faint"
        >
          Skip →
        </button>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center text-center">
        <AnimatePresence mode="wait">
          <motion.div
            key={idx}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="space-y-5"
          >
            <div className="text-8xl">{slide.icon}</div>
            <div>
              <div className="font-display-alt text-xs uppercase tracking-[0.3em] text-trophy-gold">
                {slide.eyebrow}
              </div>
              <h1 className="font-display text-4xl uppercase tracking-tight mt-1 leading-tight">
                {slide.title}
              </h1>
            </div>
            <p className="text-text-dim text-sm leading-relaxed max-w-xs mx-auto">{slide.body}</p>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-center gap-2">
          {SLIDES.map((_, i) => (
            <span
              key={i}
              className={cn(
                'h-1.5 rounded-full transition-all',
                i === idx ? 'bg-trophy-gold w-6' : 'bg-bg-elevated w-1.5',
              )}
            />
          ))}
        </div>
        <Button variant="gold" fullWidth onClick={advance}>
          {isLast ? "Let's go" : 'Next'}
        </Button>
      </div>
    </div>
  );
}
